import { useStore } from 'react-redux'
import {
  checkoutSelector,
  isRecoverableFlow,
  recoverableTerminalErrorsStateSelector,
  startedCheckoutSelector,
  useAppRouter,
  useAppSelector,
  useConsumerSelector,
  useIsBillingAgreementApprovalProcess,
  usePreferredPaymentMethod,
  useSelectedPaymentType,
} from '..'
import { useIsApplePaylater } from '../applePaylater/hooks'
import { useFlag } from '@afterpaytouch/feature'
import { Features, isOverdueResponseType, PaymentMethodType } from '../../model'
import { CheckoutAppSelector } from '../store'
import { ApplePayHelper } from './helper'
import { ApplePayState } from './reducer'
import { PaymentType } from '@afterpay/types'
import { FirstPaymentType } from '@afterpaytouch/portal-api'
import { createSelector } from '@reduxjs/toolkit'
import { differenceInDays } from 'date-fns'

export interface LineItem {
  amount: string
  count: number
  startDate?: Date
  endDate?: Date
  paymentIntervalCount?: number
}

interface TotalLineItem {
  amount: string
  date?: string
  puf: boolean
  currencyCode: string
}

export const applePayTotalLineItem: CheckoutAppSelector<TotalLineItem> = (state) => {
  const { status: recoverableFlowStatus, selectedOrder } = recoverableTerminalErrorsStateSelector(state)

  if (isRecoverableFlow(recoverableFlowStatus)) {
    if (isOverdueResponseType(selectedOrder)) {
      return {
        amount: selectedOrder.totalAmount.amount,
        currencyCode: selectedOrder.totalAmount.currency,
        date: selectedOrder.overduePaymentSchedules[0]?.installmentDueDate,
        puf: false,
      }
    }

    if (selectedOrder === undefined) {
      throw new Error('Apple Pay total needs a selected order in the recoverable flow')
    }

    return {
      amount: selectedOrder.orderSummary.totalOwed.amount,
      currencyCode: selectedOrder.orderSummary.totalOwed.currency,
      puf: false,
    }
  }

  const started = startedCheckoutSelector(state)
  if (started === undefined) {
    throw new Error('Apple Pay total needs a started checkout')
  }
  const { amountDueToday, paymentSchedules, paymentUpFront, promotion } = started
  let amount = amountDueToday.amount
  if (promotion != null && paymentUpFront) {
    const { owedAfterDiscount } = promotion.orderDiscountedPaymentSchedules[0]
    if (owedAfterDiscount === undefined) {
      throw new Error('Apple Pay total needs the discounted amount of the promotion')
    }
    amount = owedAfterDiscount.amount
  }

  return {
    amount,
    currencyCode: amountDueToday.currency,
    date: paymentSchedules.length > 0 ? paymentSchedules[0].installmentDueDate ?? undefined : undefined,
    puf: paymentUpFront,
  }
}

export const applePayLineItems: CheckoutAppSelector<LineItem[]> = (state) => {
  const { status: recoverableFlowStatus, selectedOrder } = recoverableTerminalErrorsStateSelector(state)

  if (isRecoverableFlow(recoverableFlowStatus)) {
    if (isOverdueResponseType(selectedOrder)) {
      const { overduePaymentSchedules } = selectedOrder
      const secondLastOverdue = overduePaymentSchedules[overduePaymentSchedules.length - 2]
      const startOverdueDate = new Date(overduePaymentSchedules[0].installmentDueDate)
      const endOverdueDate = new Date(overduePaymentSchedules[overduePaymentSchedules.length - 1].installmentDueDate)
      const recurringPaymentIntervalCount =
        secondLastOverdue === undefined ? undefined : differenceInDays(endOverdueDate, new Date(secondLastOverdue.installmentDueDate))

      return [
        {
          amount: overduePaymentSchedules[0].amount.amount,
          count: overduePaymentSchedules.length,
          startDate: startOverdueDate,
          endDate: endOverdueDate,
          paymentIntervalCount: recurringPaymentIntervalCount,
        },
      ]
    }

    if (selectedOrder === undefined) {
      throw new Error('Apple Pay line items need a selected order in the recoverable flow')
    }

    return [
      {
        amount: selectedOrder.orderSummary.totalOwed.amount,
        count: 1,
      },
    ]
  }

  const firstPaymentType = checkoutSelector(state)?.firstPaymentType ?? FirstPaymentType.PaymentUpfront
  const started = startedCheckoutSelector(state)
  if (started === undefined) {
    throw new Error('Apple Pay line items need a started checkout')
  }
  const { amountDueToday, paymentSchedules, promotion, paymentUpFront } = started

  let promoDiscount = 0
  if (promotion != null && paymentUpFront) {
    const { discount } = promotion.orderDiscountedPaymentSchedules[0]
    if (discount === undefined) {
      throw new Error('Apple Pay line items need the discount of the promotion')
    }
    promoDiscount = parseFloat(discount.amount)
  }

  const endPaymentDate = getDueDateAsDate(paymentSchedules, null, 1)
  const secondLastPaymentDate = getDueDateAsDate(paymentSchedules, null, 2)
  const recurringPaymentIntervalCount = differenceInDays(endPaymentDate, secondLastPaymentDate)

  if (firstPaymentType === FirstPaymentType.LargePaymentUpfront || firstPaymentType === FirstPaymentType.VariablePaymentUpfront) {
    return [
      { amount: (parseFloat(amountDueToday.amount) - promoDiscount).toFixed(2), count: 1 },
      {
        amount: paymentSchedules.length > 0 ? (parseFloat(paymentSchedules[1].amount.amount) - promoDiscount).toFixed(2) : '0.00',
        count: paymentSchedules.length - 1,
        startDate: getDueDateAsDate(paymentSchedules, 1, null),
        endDate: endPaymentDate,
        paymentIntervalCount: recurringPaymentIntervalCount,
      },
    ]
  }
  return [
    {
      amount: paymentSchedules.length > 0 ? (parseFloat(paymentSchedules[0].amount.amount) - promoDiscount).toFixed(2) : '0.00',
      count: paymentSchedules.length,
      startDate: getDueDateAsDate(paymentSchedules, 0, null),
      endDate: endPaymentDate,
      paymentIntervalCount: recurringPaymentIntervalCount,
    },
  ]
}

export const applePayBillingAgreementLineItem: CheckoutAppSelector<LineItem[]> = (state) => {
  const { paymentSchedules } = startedCheckoutSelector(state) ?? {}

  return [
    {
      amount: paymentSchedules !== undefined && paymentSchedules.length > 0 ? parseFloat(paymentSchedules[0].amount.amount).toFixed(2) : '0.00',
      count: paymentSchedules?.length ?? 0,
      startDate: getDueDateAsDate(paymentSchedules, 0, null),
    },
  ]
}

const getDueDateAsDate = (paymentSchedules, dueDateIndex, dueDateIndexFromLength): Date => {
  if (paymentSchedules.length === 0) {
    return new Date()
  }

  const dateIndex = dueDateIndex !== undefined && dueDateIndex !== null ? dueDateIndex : paymentSchedules.length - dueDateIndexFromLength
  const dueDate = paymentSchedules[dateIndex]?.installmentDueDate ?? null
  return dueDate === null ? new Date() : new Date(dueDate)
}

export const applePaySelector: CheckoutAppSelector<ApplePayState> = (state) => state.applepay

export const applePayDisableCreditCardSelector: CheckoutAppSelector<boolean> = createSelector(applePaySelector, (state) => state.isCreditCardDisabled ?? false)

export const useApplePayState = (): ApplePayState => useAppSelector(applePaySelector)

export const useApplePayFeature = (): {
  new: boolean
  global: boolean
  enabledForRecurringPayments: boolean
  enabledForConsumerLending: boolean
} => {
  const isEnabledNew = useFlag(Features.ENABLE_APPLE_PAY_FOR_NEW_CHECKOUT)
  const isEnabledGlobal = useFlag(Features.ROSELLA_PAYMENT_IN_CHECKOUT)
  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()
  const selectedPaymentType = useSelectedPaymentType() ?? PaymentType.PBI

  return {
    new: isEnabledNew,
    global: isEnabledGlobal,
    enabledForRecurringPayments: !isBillingAgreementApprovalProcess,
    enabledForConsumerLending: selectedPaymentType === PaymentType.PBI,
  }
}

export const useApplePayPayment = (): boolean => {
  const isApplePayEnabled = useApplePayFeature()
  const { isEnabledForDevice } = useApplePayState()
  const { preferredPaymentMethod } = useConsumerSelector()
  const isPreferredMethod = preferredPaymentMethod === PaymentMethodType.APPLE_PAY

  return (
    isApplePayEnabled.new &&
    isEnabledForDevice &&
    (isPreferredMethod || isApplePayEnabled.global) &&
    isApplePayEnabled.enabledForRecurringPayments &&
    isApplePayEnabled.enabledForConsumerLending
  )
}

export const useApplePayForCashCohort = (): boolean => {
  const isEnabled = useFlag(Features.ENABLE_APPLE_PAY_FOR_CASH_COHORT, false)
  const { new: isNewCheckout, enabledForRecurringPayments, enabledForConsumerLending } = useApplePayFeature()
  const { isEnabledForDevice } = useApplePayState()

  return isEnabled && isNewCheckout && isEnabledForDevice && enabledForRecurringPayments && enabledForConsumerLending
}

export const useShouldSetApplePayAsPurchaseMethod = (): boolean => {
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const isApplePayEnabled = useApplePayFeature()
  const { isEnabledForDevice } = useApplePayState()
  const isApplePaylater = useIsApplePaylater()

  if (isApplePaylater) {
    return false
  }

  return preferredPaymentMethod === PaymentMethodType.APPLE_PAY && isApplePayEnabled.new && isEnabledForDevice && isApplePayEnabled.enabledForRecurringPayments
}

export const useApplePay = (): ApplePayHelper => {
  const store = useStore()
  const { locale } = useAppRouter()

  return ApplePayHelper.getInstance({ store, locale })
}
